import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FiPaperclip, FiTrash2 } from 'react-icons/fi';
import axiosOrganizationInstance from './AxiosOrganizationInstance';
import './css/support.css';

const TYPE_OPTIONS = [
  { value: 'business', label: 'Business' },
  { value: 'restaurant', label: 'Restaurant' },
  { value: 'store', label: 'Store' },
  { value: 'organization', label: 'Organization' },
  { value: 'customer', label: 'Customer' },
];

const EMPTY_FORM = {
  issueDateTime: '',
  issuePage: '',
  contactNumber: '',
  issueDetails: '',
  status: 'New',
  type: '',
  businessName: '',
  files: [],
};

function normalizeSupportType(raw) {
  const value = String(raw || '').trim().toLowerCase();
  if (TYPE_OPTIONS.some((opt) => opt.value === value)) return value;
  return '';
}

function typeLabel(value) {
  return TYPE_OPTIONS.find((opt) => opt.value === value)?.label || value || '';
}

const Support = () => {
  const [searchParams] = useSearchParams();
  const initialType = normalizeSupportType(searchParams.get('type'));
  const initialName = String(searchParams.get('name') || '').trim();
  const lockContext = Boolean(initialType || initialName);

  const [formData, setFormData] = useState(() => ({
    ...EMPTY_FORM,
    type: initialType,
    businessName: initialName,
  }));
  const [fileErrors, setFileErrors] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files || []);
    const nextFiles = [...formData.files];
    let tooLarge = false;

    selectedFiles.forEach((file) => {
      if (file.size > 2 * 1024 * 1024) {
        tooLarge = true;
        return;
      }
      if (!nextFiles.some((f) => f.name === file.name && f.size === file.size)) {
        nextFiles.push(file);
      }
    });

    e.target.value = '';
    if (tooLarge) {
      setFileErrors('Each file must be less than 2MB.');
      return;
    }
    setFileErrors('');
    setFormData((prev) => ({ ...prev, files: nextFiles }));
  };

  const removeFile = (fileToRemove) => {
    setFormData((prev) => ({
      ...prev,
      files: prev.files.filter(
        (file) => !(file.name === fileToRemove.name && file.size === fileToRemove.size)
      ),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setSubmitting(true);

    const data = new FormData();
    data.append('issue_date_time', formData.issueDateTime);
    data.append('contact_number', formData.contactNumber);
    data.append('status', formData.status);
    data.append('type', formData.type);
    data.append('business_name', formData.businessName || '');
    data.append('issue_page', formData.issuePage);
    data.append('issue_details', formData.issueDetails);
    formData.files.forEach((file) => {
      data.append('file', file);
    });

    try {
      const token = localStorage.getItem('Token');
      await axiosOrganizationInstance.post('/organization/file', data, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${token}`,
        },
      });
      setFormData({
        ...EMPTY_FORM,
        type: initialType,
        businessName: initialName,
      });
      setSuccess('Support request submitted successfully.');
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          err?.message ||
          'Unable to submit support request.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="maya-support-page">
      <main className="maya-support-main">
        <h1 className="maya-support-title">Support</h1>
        <p className="maya-support-subtitle">
          Tell us what went wrong and we will follow up.
        </p>

        <form className="maya-support-panel" onSubmit={handleSubmit}>
          {error ? <div className="maya-support-alert maya-support-alert--error">{error}</div> : null}
          {success ? <div className="maya-support-alert maya-support-alert--success">{success}</div> : null}

          <div className="maya-support-grid">
            <label className="maya-support-field">
              <span>Business name</span>
              <input
                type="text"
                name="businessName"
                value={formData.businessName}
                onChange={handleChange}
                placeholder="Restaurant, store, or organization name"
                readOnly={lockContext}
                required
                className={lockContext ? 'maya-support-input--locked' : undefined}
              />
            </label>

            <label className="maya-support-field">
              <span>Type</span>
              {lockContext ? (
                <>
                  <input
                    type="text"
                    value={typeLabel(formData.type)}
                    readOnly
                    className="maya-support-input--locked"
                    required
                  />
                  <input type="hidden" name="type" value={formData.type} />
                </>
              ) : (
                <select name="type" value={formData.type} onChange={handleChange} required>
                  <option value="">Select type</option>
                  {TYPE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              )}
            </label>

            <label className="maya-support-field">
              <span>Issue date &amp; time</span>
              <input
                type="datetime-local"
                name="issueDateTime"
                value={formData.issueDateTime}
                onChange={handleChange}
                required
              />
            </label>

            <label className="maya-support-field">
              <span>Contact number</span>
              <input
                type="tel"
                name="contactNumber"
                value={formData.contactNumber}
                onChange={handleChange}
                placeholder="Mobile number"
                required
              />
            </label>

            <label className="maya-support-field maya-support-field--full">
              <span>Issue page</span>
              <input
                type="text"
                name="issuePage"
                value={formData.issuePage}
                onChange={handleChange}
                placeholder="Page or screen name (optional)"
              />
            </label>

            <label className="maya-support-field maya-support-field--full">
              <span>Issue details</span>
              <textarea
                name="issueDetails"
                rows={3}
                value={formData.issueDetails}
                onChange={handleChange}
                placeholder="Describe the issue"
              />
            </label>

            <div className="maya-support-field maya-support-field--full">
              <span>Attachments</span>
              <label className="maya-support-file">
                <FiPaperclip aria-hidden />
                <span>Add files (max 2MB each)</span>
                <input type="file" multiple onChange={handleFileChange} />
              </label>
              {fileErrors ? <div className="maya-support-alert maya-support-alert--error">{fileErrors}</div> : null}
              {formData.files.length > 0 ? (
                <ul className="maya-support-files">
                  {formData.files.map((file) => (
                    <li key={`${file.name}-${file.size}`}>
                      <span>
                        {file.name} ({(file.size / 1024).toFixed(0)} KB)
                      </span>
                      <button
                        type="button"
                        className="maya-support-file-remove"
                        onClick={() => removeFile(file)}
                        aria-label={`Remove ${file.name}`}
                      >
                        <FiTrash2 aria-hidden />
                      </button>
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          </div>

          <div className="maya-support-actions">
            <button type="button" className="maya-support-btn maya-support-btn--secondary" onClick={() => window.close()}>
              Cancel
            </button>
            <button type="submit" className="maya-support-btn maya-support-btn--primary" disabled={submitting}>
              {submitting ? 'Submitting...' : 'Submit request'}
            </button>
          </div>
        </form>
      </main>
    </div>
  );
};

export default Support;
